<!--
  The type is chosen first (EventTypePicker); this renders only that type's own fields -
  thirteen small honest blocks, never one form with every field greyed out (AGENTS.md
  garage note). `modelValue` is always a fully-formed `CarEventData` for the current
  `type`: on mount it seeds `local` from `modelValue` only when `isEditing` (a real
  recorded event), otherwise blank; a later `type` change always rebuilds from scratch
  rather than carrying over stray fields from the previous type.
-->
<script setup lang="ts">
import type {
  Axle,
  Car,
  CarEventData,
  CarEventType,
  FineStatus,
  FineType,
  FuelType,
  InspectionResult,
  InsurancePolicy,
  MaintenanceSchedule,
  TireType,
  WashType,
} from '../internal/types.ts'
import { formatLocalDate, parseLocalDate, type LocalDate } from '@taler/domain'
import { Select } from '@taler/ui'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  type: CarEventType
  modelValue: CarEventData
  car: Car
  policies: readonly InsurancePolicy[]
  schedules: readonly MaintenanceSchedule[]
  isEditing: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [CarEventData] }>()

const { t } = useI18n()

function strOrUndef(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function parseDateOrUndef(value: string): LocalDate | undefined {
  if (value === '') return undefined
  try {
    return parseLocalDate(value)
  } catch {
    return undefined
  }
}

interface LocalFields {
  fuelAmount: string
  fuelStation: string
  fuelTypeOverride: FuelType | undefined
  repairShop: string
  description: string
  items: string
  fulfilledScheduleIds: string[]
  policyId: string | undefined
  installmentNumber: string
  taxType: string | undefined
  taxPeriod: string
  authority: string
  referenceNumber: string
  station: string
  result: InspectionResult | undefined
  validUntil: string
  expiryDate: string
  washType: WashType | undefined
  location: string
  durationMinutes: string
  fineType: FineType | undefined
  status: FineStatus | undefined
  dueDate: string
  buyerNotes: string
  sellerNotes: string
  axle: Axle | undefined
  tireType: TireType | undefined
  brand: string
  shop: string
}

function blankFields(): LocalFields {
  return {
    fuelAmount: '',
    fuelStation: '',
    fuelTypeOverride: undefined,
    repairShop: '',
    description: '',
    items: '',
    fulfilledScheduleIds: [],
    policyId: undefined,
    installmentNumber: '',
    taxType: undefined,
    taxPeriod: '',
    authority: '',
    referenceNumber: '',
    station: '',
    result: undefined,
    validUntil: '',
    expiryDate: '',
    washType: undefined,
    location: '',
    durationMinutes: '',
    fineType: undefined,
    status: undefined,
    dueDate: '',
    buyerNotes: '',
    sellerNotes: '',
    axle: undefined,
    tireType: undefined,
    brand: '',
    shop: '',
  }
}

// Plain local fields for every type - simplest binding surface for a form this wide; only
// the branch matching `props.type` ever reads or writes its own slice.
const local = reactive<LocalFields>(blankFields())
const fuelOverrideExpanded = ref(false)

/** Inverse of `build()` - seeds `local` from an already-recorded `CarEventData` (editing an
 * event) instead of discarding it, matched field-for-field against the type it carries. */
function fieldsFromData(data: CarEventData): LocalFields {
  const fields = blankFields()
  switch (data.type) {
    case 'FUEL':
      fields.fuelAmount = data.fuelAmount ?? ''
      fields.fuelStation = data.fuelStation ?? ''
      fields.fuelTypeOverride = data.fuelTypeOverride
      return fields
    case 'REPAIR':
      fields.repairShop = data.repairShop ?? ''
      fields.description = data.description ?? ''
      fields.items = data.items.join(', ')
      fields.fulfilledScheduleIds = [...data.fulfilledScheduleIds]
      return fields
    case 'INSURANCE_PAYMENT':
      fields.policyId = data.policyId
      fields.installmentNumber = data.installmentNumber === undefined ? '' : String(data.installmentNumber)
      return fields
    case 'TAX':
      fields.taxType = data.taxType
      fields.taxPeriod = data.taxPeriod ?? ''
      fields.authority = data.authority ?? ''
      fields.referenceNumber = data.referenceNumber ?? ''
      return fields
    case 'INSPECTION':
      fields.station = data.station ?? ''
      fields.result = data.result
      fields.validUntil = data.validUntil === undefined ? '' : formatLocalDate(data.validUntil)
      return fields
    case 'REGISTRATION':
      fields.authority = data.authority ?? ''
      fields.expiryDate = data.expiryDate === undefined ? '' : formatLocalDate(data.expiryDate)
      return fields
    case 'CAR_WASH':
      fields.washType = data.washType
      return fields
    case 'PARKING':
      fields.location = data.location ?? ''
      fields.durationMinutes = data.durationMinutes === undefined ? '' : String(data.durationMinutes)
      return fields
    case 'FINE':
      fields.fineType = data.fineType
      fields.status = data.status
      fields.authority = data.authority ?? ''
      fields.referenceNumber = data.referenceNumber ?? ''
      fields.dueDate = data.dueDate === undefined ? '' : formatLocalDate(data.dueDate)
      return fields
    case 'MILEAGE_LOG':
      return fields
    case 'SELL':
      fields.buyerNotes = data.buyerNotes ?? ''
      return fields
    case 'BUY':
      fields.sellerNotes = data.sellerNotes ?? ''
      return fields
    case 'TIRE_CHANGE':
      fields.axle = data.axle
      fields.tireType = data.tireType
      fields.brand = data.brand ?? ''
      fields.shop = data.shop ?? ''
      return fields
  }
}

watch(
  () => props.type,
  (_type, previousType) => {
    Object.assign(
      local,
      previousType === undefined && props.isEditing ? fieldsFromData(props.modelValue) : blankFields(),
    )
    fuelOverrideExpanded.value = local.fuelTypeOverride !== undefined
    emit('update:modelValue', build())
  },
  { immediate: true },
)

function build(): CarEventData {
  switch (props.type) {
    case 'FUEL':
      return {
        type: 'FUEL',
        fuelAmount: local.fuelAmount === '' ? undefined : local.fuelAmount,
        fuelStation: strOrUndef(local.fuelStation),
        fuelTypeOverride: local.fuelTypeOverride,
      }
    case 'REPAIR':
      return {
        type: 'REPAIR',
        repairShop: strOrUndef(local.repairShop),
        description: strOrUndef(local.description),
        items: local.items
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== ''),
        fulfilledScheduleIds: [...local.fulfilledScheduleIds],
      }
    case 'INSURANCE_PAYMENT':
      return {
        type: 'INSURANCE_PAYMENT',
        policyId: local.policyId,
        installmentNumber: local.installmentNumber === '' ? undefined : parseInt(local.installmentNumber, 10),
      }
    case 'TAX':
      return {
        type: 'TAX',
        taxType: local.taxType,
        taxPeriod: strOrUndef(local.taxPeriod),
        authority: strOrUndef(local.authority),
        referenceNumber: strOrUndef(local.referenceNumber),
      }
    case 'INSPECTION':
      return {
        type: 'INSPECTION',
        station: strOrUndef(local.station),
        result: local.result,
        validUntil: parseDateOrUndef(local.validUntil),
      }
    case 'REGISTRATION':
      return {
        type: 'REGISTRATION',
        authority: strOrUndef(local.authority),
        expiryDate: parseDateOrUndef(local.expiryDate),
      }
    case 'CAR_WASH':
      return { type: 'CAR_WASH', washType: local.washType }
    case 'PARKING':
      return {
        type: 'PARKING',
        location: strOrUndef(local.location),
        durationMinutes: local.durationMinutes === '' ? undefined : parseInt(local.durationMinutes, 10),
      }
    case 'FINE':
      return {
        type: 'FINE',
        fineType: local.fineType,
        status: local.status,
        authority: strOrUndef(local.authority),
        referenceNumber: strOrUndef(local.referenceNumber),
        dueDate: parseDateOrUndef(local.dueDate),
      }
    case 'MILEAGE_LOG':
      return { type: 'MILEAGE_LOG' }
    case 'SELL':
      return { type: 'SELL', buyerNotes: strOrUndef(local.buyerNotes) }
    case 'BUY':
      return { type: 'BUY', sellerNotes: strOrUndef(local.sellerNotes) }
    case 'TIRE_CHANGE':
      return {
        type: 'TIRE_CHANGE',
        axle: local.axle,
        tireType: local.tireType,
        brand: strOrUndef(local.brand),
        shop: strOrUndef(local.shop),
      }
  }
}

watch(local, () => emit('update:modelValue', build()), { deep: true })

const fuelTypeOptions = computed(() => [
  { value: 'DIESEL' as const, label: t('garage.fuelType.DIESEL') },
  { value: 'PETROL' as const, label: t('garage.fuelType.PETROL') },
  { value: 'ELECTRIC' as const, label: t('garage.fuelType.ELECTRIC') },
  { value: 'HYBRID' as const, label: t('garage.fuelType.HYBRID') },
  { value: 'GAS' as const, label: t('garage.fuelType.GAS') },
])
const inspectionResultOptions = computed(() => [
  { value: 'PASS' as const, label: t('garage.inspectionResult.PASS') },
  { value: 'FAIL' as const, label: t('garage.inspectionResult.FAIL') },
  { value: 'CONDITIONAL' as const, label: t('garage.inspectionResult.CONDITIONAL') },
])
const washTypeOptions = computed(() => [
  { value: 'SELF_SERVICE' as const, label: t('garage.washType.SELF_SERVICE') },
  { value: 'AUTOMATIC' as const, label: t('garage.washType.AUTOMATIC') },
  { value: 'HAND_WASH' as const, label: t('garage.washType.HAND_WASH') },
  { value: 'DETAILING' as const, label: t('garage.washType.DETAILING') },
])
const fineTypeOptions = computed(() => [
  { value: 'SPEEDING' as const, label: t('garage.fineType.SPEEDING') },
  { value: 'PARKING' as const, label: t('garage.fineType.PARKING') },
  { value: 'OTHER' as const, label: t('garage.fineType.OTHER') },
])
const fineStatusOptions = computed(() => [
  { value: 'UNPAID' as const, label: t('garage.fineStatus.UNPAID') },
  { value: 'PAID' as const, label: t('garage.fineStatus.PAID') },
  { value: 'CONTESTED' as const, label: t('garage.fineStatus.CONTESTED') },
])
const taxTypeOptions = computed(() => [
  { value: 'WEIGHT_TAX', label: t('garage.taxType.WEIGHT_TAX') },
  { value: 'ROAD_TOLL', label: t('garage.taxType.ROAD_TOLL') },
  { value: 'OTHER', label: t('garage.taxType.OTHER') },
])
const tireTypeOptions = computed(() => [
  { value: 'SUMMER' as const, label: t('garage.tireType.SUMMER') },
  { value: 'WINTER' as const, label: t('garage.tireType.WINTER') },
  { value: 'ALL_SEASON' as const, label: t('garage.tireType.ALL_SEASON') },
])
const axleOptions = computed(() => [
  { value: 'FRONT' as const, label: t('garage.axle.FRONT') },
  { value: 'REAR' as const, label: t('garage.axle.REAR') },
  { value: 'ALL' as const, label: t('garage.axle.ALL') },
])
const policyOptions = computed(() =>
  props.policies.map((policy) => ({
    value: policy.id,
    label: `${policy.insurer} – ${policy.policyNumber ?? '—'}`,
  })),
)

function useCarFuelType(): void {
  local.fuelTypeOverride = undefined
  fuelOverrideExpanded.value = false
}

function toggleSchedule(id: string, checked: boolean): void {
  local.fulfilledScheduleIds = checked
    ? [...local.fulfilledScheduleIds, id]
    : local.fulfilledScheduleIds.filter((candidate) => candidate !== id)
}

const FIELD_LABEL_CLASS = 'text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]'
const FIELD_INPUT_CLASS =
  'h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]'
</script>

<template>
  <div
    class="grid grid-cols-2 gap-[var(--space-cell)]"
    data-testid="event-type-fields"
    :data-event-type="type"
  >
    <template v-if="type === 'FUEL'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.fuelAmount') }}</span>
        <input v-model="local.fuelAmount" type="number" step="0.01" min="0" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.fuelStation') }}</span>
        <input v-model="local.fuelStation" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <div class="col-span-2 flex flex-col gap-1">
        <template v-if="fuelOverrideExpanded">
          <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.fuelTypeOverride') }}</span>
          <Select
            v-model="local.fuelTypeOverride"
            :options="fuelTypeOptions"
            :placeholder="t('garage.eventField.selectDefault')"
          />
          <button
            type="button"
            class="self-start text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            @click="useCarFuelType"
          >
            {{ t('garage.eventField.fuelTypeUseDefault') }}
          </button>
        </template>
        <p
          v-else
          class="flex items-center gap-2 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
        >
          {{ t('garage.eventField.fuelTypeDefault', { fuelType: t(`garage.fuelType.${car.fuelType}`) }) }}
          <button
            type="button"
            class="text-[color:var(--color-text-muted)] underline"
            @click="fuelOverrideExpanded = true"
          >
            {{ t('garage.eventField.fuelTypeOverrideToggle') }}
          </button>
        </p>
      </div>
    </template>

    <template v-else-if="type === 'REPAIR'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.repairShop') }}</span>
        <input v-model="local.repairShop" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="col-span-2 flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.description') }}</span>
        <textarea v-model="local.description" rows="2" :class="[FIELD_INPUT_CLASS, 'h-auto py-2']" />
      </label>
      <label class="col-span-2 flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS"
          >{{ t('garage.eventField.items') }}
          <span class="text-[color:var(--color-text-muted)]"
            >({{ t('garage.eventField.itemsHint') }})</span
          ></span
        >
        <input v-model="local.items" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <fieldset v-if="schedules.length > 0" class="col-span-2 flex flex-col gap-1">
        <legend :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.fulfilledScheduleIds') }}</legend>
        <label
          v-for="schedule in schedules"
          :key="schedule.id"
          class="flex items-center gap-2 text-[color:var(--color-text)]"
        >
          <input
            type="checkbox"
            :checked="local.fulfilledScheduleIds.includes(schedule.id)"
            @change="toggleSchedule(schedule.id, ($event.target as HTMLInputElement).checked)"
          />
          {{ schedule.name }}
        </label>
      </fieldset>
    </template>

    <template v-else-if="type === 'INSURANCE_PAYMENT'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.policyId') }}</span>
        <Select
          v-model="local.policyId"
          :options="policyOptions"
          :placeholder="t('garage.eventField.selectPolicy')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.installmentNumber') }}</span>
        <input v-model="local.installmentNumber" type="number" min="1" :class="FIELD_INPUT_CLASS" />
      </label>
    </template>

    <template v-else-if="type === 'TAX'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.taxType') }}</span>
        <Select
          v-model="local.taxType"
          :options="taxTypeOptions"
          :placeholder="t('garage.policies.selectType')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.taxPeriod') }}</span>
        <input v-model="local.taxPeriod" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.authority') }}</span>
        <input v-model="local.authority" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.referenceNumber') }}</span>
        <input v-model="local.referenceNumber" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
    </template>

    <template v-else-if="type === 'INSPECTION'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.station') }}</span>
        <input v-model="local.station" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.result') }}</span>
        <Select
          v-model="local.result"
          :options="inspectionResultOptions"
          :placeholder="t('garage.policies.selectType')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.validUntil') }}</span>
        <input v-model="local.validUntil" type="date" :class="FIELD_INPUT_CLASS" />
      </label>
    </template>

    <template v-else-if="type === 'REGISTRATION'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.authority') }}</span>
        <input v-model="local.authority" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.expiryDate') }}</span>
        <input v-model="local.expiryDate" type="date" :class="FIELD_INPUT_CLASS" />
      </label>
    </template>

    <template v-else-if="type === 'CAR_WASH'">
      <label class="col-span-2 flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.washType') }}</span>
        <Select
          v-model="local.washType"
          :options="washTypeOptions"
          :placeholder="t('garage.policies.selectType')"
        />
      </label>
    </template>

    <template v-else-if="type === 'PARKING'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.location') }}</span>
        <input v-model="local.location" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.durationMinutes') }}</span>
        <input v-model="local.durationMinutes" type="number" min="0" :class="FIELD_INPUT_CLASS" />
      </label>
    </template>

    <template v-else-if="type === 'FINE'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.fineType') }}</span>
        <Select
          v-model="local.fineType"
          :options="fineTypeOptions"
          :placeholder="t('garage.policies.selectType')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.status') }}</span>
        <Select
          v-model="local.status"
          :options="fineStatusOptions"
          :placeholder="t('garage.policies.selectType')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.authority') }}</span>
        <input v-model="local.authority" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.referenceNumber') }}</span>
        <input v-model="local.referenceNumber" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.dueDate') }}</span>
        <input v-model="local.dueDate" type="date" :class="FIELD_INPUT_CLASS" />
      </label>
    </template>

    <template v-else-if="type === 'SELL'">
      <label class="col-span-2 flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.buyerNotes') }}</span>
        <textarea v-model="local.buyerNotes" rows="2" :class="[FIELD_INPUT_CLASS, 'h-auto py-2']" />
      </label>
    </template>

    <template v-else-if="type === 'BUY'">
      <label class="col-span-2 flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.sellerNotes') }}</span>
        <textarea v-model="local.sellerNotes" rows="2" :class="[FIELD_INPUT_CLASS, 'h-auto py-2']" />
      </label>
    </template>

    <template v-else-if="type === 'TIRE_CHANGE'">
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.axle') }}</span>
        <Select v-model="local.axle" :options="axleOptions" :placeholder="t('garage.policies.selectType')" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.tireType') }}</span>
        <Select
          v-model="local.tireType"
          :options="tireTypeOptions"
          :placeholder="t('garage.policies.selectType')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.brand') }}</span>
        <input v-model="local.brand" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
      <label class="flex flex-col gap-1">
        <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.shop') }}</span>
        <input v-model="local.shop" type="text" :class="FIELD_INPUT_CLASS" />
      </label>
    </template>
    <!-- MILEAGE_LOG has no type-specific fields at all. -->
  </div>
</template>
